'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Sun, Moon } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { ProtectedImage } from '@/components/ProtectedImage/ProtectedImage';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const pathname = usePathname();
  const router = useRouter();
  const { isAuthenticated, signOut, userProfile } = useAuth();
  const { theme, toggle } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Scroll listener for transparent header effect
  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationItems = [
    { label: 'RESIDENTIAL REAL ESTATE', href: '/homepage' },
  ];

  const serviceItems = [
    { label: 'Image Enhancement', href: '/services/image-enhancement' },
    { label: 'Virtual Staging', href: '/services/virtual-staging' },
    { label: 'Day to Dusk', href: '/services/day-to-dusk' },
    { label: 'Item Removal', href: '/services/item-removal' },
    { label: 'Floor Plans', href: '/services/floor-plans' },
    { label: 'Virtual Renovation', href: '/services/virtual-renovation' },
  ];

  const isActive = (href: string) => pathname === href;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    await signOut();
    router.push('/auth/signin');
  };

  // Get dashboard URL based on user role
  const getDashboardUrl = () => {
    if (!userProfile?.role) return '/client-dashboard';
    
    switch (userProfile.role.toLowerCase()) {
      case 'admin':
        return '/admin-control-panel';
      case 'editor':
        return '/editor-workspace';
      case 'client':
      default:
        return '/client-dashboard';
    }
  };

  return (
    <>
      {/* Main Header - Dark Theme with scroll-based transparency */}
      <div 
        className={`sticky top-0 z-50 ${className} backdrop-blur-xl transition-all duration-300`}
        style={{
          backgroundColor: isScrolled ? 'rgba(11, 15, 26, 0.75)' : 'rgba(11, 15, 26, 0.85)',
          borderBottom: `1px solid rgba(255, 255, 255, ${isScrolled ? '0.05' : '0.1'})`,
        }}
      >
      <div className="w-full max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between h-16 px-6 lg:px-12">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center flex-shrink-0 group">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/shashio-logo.svg"
              alt="Shashio"
              className="h-7 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Main Navigation - Center with underline animation */}
          <nav className="hidden lg:flex items-center space-x-6 flex-1 justify-center">
            <Link
              href="/homepage"
              className={`relative text-sm font-medium transition-all duration-200 group ${
                isActive('/homepage') ? 'text-white' : 'text-gray-300 hover:text-white'
              }`}
            >
              Features
              <span className={`absolute -bottom-2 left-0 right-0 h-0.5 bg-purple-500 transform transition-all duration-300 ${
                isActive('/homepage') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
              }`}></span>
            </Link>
            
            {/* Services Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button
                className={`relative text-sm font-medium transition-all duration-200 flex items-center space-x-1 ${
                  pathname.includes('/services') ? 'text-white' : 'text-gray-300 hover:text-white'
                }`}
              >
                <span>Services</span>
                <Icon name="ChevronDownIcon" size={14} variant="outline" className={`transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              <span className={`absolute -bottom-2 left-0 right-0 h-0.5 bg-purple-500 transform transition-all duration-300 ${
                pathname.includes('/services') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
              }`}></span>
              
              {/* Dropdown Menu */}
              {isServicesOpen && (
                <div 
                  className="absolute top-full left-0 pt-2 w-64 animate-slideDown"
                  style={{ zIndex: 50 }}
                >
                  <div 
                    className="rounded-xl shadow-2xl overflow-hidden"
                    style={{ 
                      backgroundColor: '#1A1F35',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <div className="p-2">
                      {serviceItems.map((service) => (
                        <Link
                          key={service.href}
                          href={service.href}
                          className={`block px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                            pathname === service.href
                              ? 'bg-purple-600/20 text-white'
                              : 'text-gray-400 hover:bg-white/5 hover:text-white'
                          }`}
                        >
                          {service.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link
              href="/services"
              className={`relative text-sm font-medium transition-all duration-200 group ${
                pathname.includes('/pricing') ? 'text-white' : 'text-gray-300 hover:text-white'
              }`}
            >
              Pricing
              <span className={`absolute -bottom-2 left-0 right-0 h-0.5 bg-purple-500 transform transition-all duration-300 ${
                pathname.includes('/pricing') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
              }`}></span>
            </Link>

            <Link
              href="/about"
              className={`relative text-sm font-medium transition-all duration-200 group ${
                isActive('/about') ? 'text-white' : 'text-gray-300 hover:text-white'
              }`}
            >
              About Us
              <span className={`absolute -bottom-2 left-0 right-0 h-0.5 bg-purple-500 transform transition-all duration-300 ${
                isActive('/about') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
              }`}></span>
            </Link>

            <Link
              href="/contact"
              className={`relative text-sm font-medium transition-all duration-200 group ${
                isActive('/contact') ? 'text-white' : 'text-gray-300 hover:text-white'
              }`}
            >
              Contact
              <span className={`absolute -bottom-2 left-0 right-0 h-0.5 bg-purple-500 transform transition-all duration-300 ${
                isActive('/contact') ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
              }`}></span>
            </Link>
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-3">
            {isAuthenticated ? (
              <div className="hidden lg:flex items-center space-x-3">
                <Link href={getDashboardUrl()}>
                  <button className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-all duration-200">
                    Dashboard
                  </button>
                </Link>
                <button
                  onClick={handleSignOut}
                  disabled={isSigningOut}
                  className="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 text-gray-300 hover:text-white"
                >
                  {isSigningOut ? 'Signing Out...' : 'Sign Out'}
                </button>
              </div>
            ) : (
              <div className="hidden lg:flex items-center space-x-3">
                <Link href="/auth/signin">
                  <button className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-all duration-200">
                    Sign In
                  </button>
                </Link>
                <Link href="/auth/signup">
                  <button 
                    className="px-6 py-2.5 text-sm font-bold rounded-lg transition-all duration-300 flex items-center gap-1.5 shadow-lg hover:shadow-xl hover:scale-105"
                    style={{
                      backgroundColor: '#7C3AED',
                      color: '#ffffff',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#6D28D9'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#7C3AED'}
                  >
                    Start Free Trial
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-lg transition-all duration-200"
              aria-label="Toggle mobile menu"
              style={{ color: '#9CA3AF' }}
            >
              <Icon
                name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
                size={24}
                variant="outline"
              />
            </button>
          </div>
        </div>
        
        {/* Add dropdown animation styles */}
        <style jsx>{`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-slideDown {
            animation: slideDown 0.2s ease-out;
          }
        `}</style>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            className="lg:hidden border-t"
            style={{
              backgroundColor: '#0B0F1A',
              borderColor: 'rgba(255, 255, 255, 0.1)',
            }}
          >
            <nav className="px-4 py-4 space-y-2">
              <Link
                href="/homepage"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-200"
              >
                Features
              </Link>
              
              {/* Services Section in Mobile */}
              <div>
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-200"
                >
                  <span>Services</span>
                  <Icon name="ChevronDownIcon" size={16} variant="outline" className={`transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {isServicesOpen && (
                  <div className="mt-1 ml-4 space-y-1">
                    {serviceItems.map((service) => (
                      <Link
                        key={service.href}
                        href={service.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block px-4 py-2.5 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-all duration-200"
                      >
                        {service.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                href="/services"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-200"
              >
                Pricing
              </Link>

              <Link
                href="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-200"
              >
                About Us
              </Link>

              <Link
                href="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-200"
              >
                Contact
              </Link>

              <div className="pt-3 space-y-2 border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
                {isAuthenticated ? (
                  <>
                    <Link href={getDashboardUrl()}>
                      <button 
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="w-full px-4 py-2.5 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-200"
                      >
                        Dashboard
                      </button>
                    </Link>
                    <button
                      onClick={() => {
                        handleSignOut();
                        setIsMobileMenuOpen(false);
                      }}
                      disabled={isSigningOut}
                      className="w-full px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all duration-200"
                      style={{
                        backgroundColor: isSigningOut ? '#4B5563' : '#6B7280',
                      }}
                    >
                      {isSigningOut ? 'Signing Out...' : 'Sign Out'}
                    </button>
                  </>
                ) : (
                  <>
                    <Link href="/auth/signin">
                      <button 
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="w-full px-4 py-2.5 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/5 transition-all duration-200"
                      >
                        Login
                      </button>
                    </Link>
                    <Link href="/auth/signup">
                      <button 
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all duration-200"
                        style={{ backgroundColor: '#7C3AED' }}
                      >
                        Get Started
                      </button>
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default Header;
